/**
 * Yelp Fusion v3 Adapter (Story 23.5) — OPENS Epic 23 Yelp slice.
 *
 * SEVENTH per-user-supplied API-key family in our adapter library (after
 * Ahrefs 20.1 + Semrush 20.4 + Moz 20.7 + CallRail 22.1 + CTM 22.3 +
 * Trustpilot 23.3). API Key (Bearer) auth, NO OAuth handshake.
 *
 * # SIX divergences from Trustpilot 23.3 (closest precedent):
 *
 *   1. **ONE secret** in connect dialog (API Key + business URL/alias/ID) —
 *      single password-masked input. Mirrors CallRail 22.1's single-input
 *      shape, NOT Trustpilot's 3-input bundle. No client_credentials dance.
 *   2. **PERMANENT access-token TTL** — Yelp API Keys never expire
 *      (customer-controlled rotation only via the Yelp Developer dashboard).
 *      `OAUTH_FAMILIES["yelp"].accessTokenTtlSec === "permanent"`.
 *   3. **PR-1 STUB recoveryRefresh** — no automatic recovery possible (Yelp
 *      ships ONE secret with no rotation-recovery mechanism). The customer
 *      re-pastes the new key in the Integration Picker.
 *   4. **NO proactive sweep** (`needsProactiveRefresh: false`) — no expiry
 *      to refresh against.
 *   5. **NEW D1 table `yelp_rating_snapshots`** — Yelp Fusion has NO
 *      historical API; the adapter snapshots `rating` + `review_count`
 *      daily. SECOND adapter with snapshot persistence (after Moz 20.7).
 *   6. **NEW per-tenant daily-call-budget circuit-breaker** at
 *      `yelp:budget:{tenantId}:{YYYY-MM-DD}`. FIRST adapter with a
 *      per-tenant per-day CALL budget (250/day ceiling on 300/day Starter).
 *
 * # THREE structural concerns:
 *
 *  - **Paid-tier gating for reviews endpoint** — Starter has NO reviews
 *    access; Plus / Enterprise unlock 3-7 most-recent excerpts.
 *    Connect-time probe sets `metadata.reviewsEndpointEntitled`;
 *    `fetchMetrics` skips the reviews endpoint when false.
 *  - **Reviewer PII discipline** — `user.name`, `user.id`,
 *    `user.profile_url`, `user.image_url`, review `text`, `phone`,
 *    `display_phone`. `sanitizeYelpPii` redacts these before any
 *    log/Sentry breadcrumb.
 *  - **3-7 most-recent-excerpts architectural cap** — Yelp returns ONLY
 *    the 3 (Enhanced) or 7 (Premium) most recent reviews with ~160-char
 *    text excerpts. Full review history is architecturally inaccessible
 *    (NOT a retention cap).
 *
 * # API_KEY_PLATFORMS divergence
 *
 * `authType: "api-key"` (mirroring all 6 prior per-user-API-key families)
 * but **DO NOT** add `"yelp"` to `API_KEY_PLATFORMS` in `adapter.ts`. That
 * Set is reserved for env-binding API-key platforms (currently
 * `["google-pagespeed"]`); Yelp is per-user-secret like the other 6.
 *
 * # GreenLife Wellness pre-wired posture
 *
 * Story 23.5 does NOT touch `demo-accounts.ts`. GreenLife already has
 * `"yelp"` in `platforms[]` AND `"yelp": "demo-1800005"` in `accountIds`
 * (pre-wired by an earlier story). Mirrors Trustpilot 23.3's pre-wired
 * posture.
 *
 * # NO reply-state surface
 *
 * Yelp Fusion's `/businesses/{id}/reviews` response shape lacks
 * `owner_response` / `companyReply` fields. The adapter ships NO
 * reply-latency / reply-rate derived metrics. Story 23.8
 * (Review Response Tracking) cross-platform widget MUST exclude Yelp
 * from its reply-rate normalizer.
 *
 * @see _bmad-output/implementation-artifacts/23-5-yelp-adapter.md
 * @see _bmad/bmm/data/integration-metrics/yelp.md
 */
import type {
  AdapterContext,
  AdapterMetrics,
  AdapterResult,
  DimensionDefinition,
  EndpointDiscoveryInfo,
  IntegrationAdapter,
  MetricDefinition,
  RateLimitConfig,
  AccountInfo,
} from "../adapter";
import { adapterErr, adapterOk } from "../adapter";
import { API_VERSIONS } from "../config";

import {
  fetchYelpBusinessDetails,
  fetchYelpReviews,
  YELP_API_BASE,
  type YelpReviewsResponse,
} from "./api";
import {
  checkYelpDailyBudget,
  incrementYelpDailyBudget,
  incrementYelpHealthcheckBudget,
  YELP_DAILY_BUDGET_CEILING,
  type YelpBudgetKv,
} from "./budget";
import { sanitizeYelpErrorMessage } from "./errors";
import { sanitizeYelpPii } from "./pii";
import { YELP_DIMENSIONS, YELP_METRICS } from "./metric-allowlist";
import {
  readYelpRatingSnapshotPriorDay,
  readYelpRatingSnapshots,
  utcYyyyMmDd,
  writeYelpRatingSnapshot,
  type YelpD1Binding,
} from "./snapshots";
import {
  buildYelpReviewAggregation,
  transformYelpBusinessDetails,
  transformYelpRatingSnapshot,
  transformYelpReview,
} from "./transformers";

const PLATFORM = "yelp" as const;
// (No adapter-side timeout — safeFetch has a 25s default that's adequate for
// Yelp's small payloads; tightening per-call is a future hardening if needed.)

/** Re-export the API base + version so downstream consumers can build URLs. */
export { YELP_API_BASE };
export const YELP_API_VERSION = API_VERSIONS.yelp;

/** Tuple literal for adapter registration parity. */
export const YELP_PLATFORM_VALUES = ["yelp"] as const;
export type YelpPlatform = (typeof YELP_PLATFORM_VALUES)[number];

const RATE_LIMITS: RateLimitConfig = {
  // 30/min conservative — Yelp's binding constraint is per-DAY (300 Starter).
  // The per-minute ceiling is defensive against runaway loops.
  requestsPerMinute: 30,
  burstLimit: 5,
  // FIRST adapter with `requestsPerDay`. Starter ceiling per
  // docs.developer.yelp.com/docs/fusion-rate-limiting verified 2026-05-18.
  requestsPerDay: 300,
};

// ============================================================================
// Metadata read (KV side-channel)
// ============================================================================

/**
 * Persisted at connect time in `yelp:meta:{integrationId}`. Mirrors the
 * Moz 20.7 / CallRail 22.1 / CTM 22.3 / Trustpilot 23.3 KV side-channel
 * pattern (the `integrations` table has no `metadata` JSON column).
 */
export interface YelpMetadata {
  readonly businessId: string;
  readonly name: string;
  readonly alias: string;
  readonly url: string;
  readonly reviewsEndpointEntitled: boolean;
  /**
   * Round 2 review fix (Edge Case Hunter): ISO 8601 timestamp of the most
   * recent 403 observed on the reviews endpoint. Used by the 2-strike
   * threshold so a SINGLE transient 403 (CDN edge mis-routing / brief
   * tier-system glitch) doesn't permanently downgrade a paid-tier customer
   * to Starter mode. Two consecutive 403s within `RECENT_403_WINDOW_MS`
   * are required to flip `reviewsEndpointEntitled` to false.
   */
  readonly last403At?: string;
  readonly address?: {
    readonly city?: string;
    readonly state?: string;
    readonly country?: string;
    readonly zipCode?: string;
  };
  readonly categories?: ReadonlyArray<{ readonly alias: string; readonly title: string }>;
  readonly price?: string | null;
  readonly isClaimed?: boolean | null;
  readonly isClosed?: boolean;
  readonly coordinates?: { readonly latitude: number; readonly longitude: number };
  readonly tier?: "starter" | "plus" | "enterprise" | "unknown";
  readonly discoveredAt?: string;
  readonly businessInput?: string;
}

/**
 * Round 2 review fix (Edge Case Hunter): 24-hour window for the 2-strike
 * 403 threshold. A 403 followed by another 403 within this window flips
 * `reviewsEndpointEntitled` to false; a stale prior 403 (>24h ago) is
 * treated as transient and only the timestamp is updated.
 */
const RECENT_403_WINDOW_MS = 24 * 60 * 60 * 1000;

interface KvGetBinding {
  get(key: string, opts?: { type?: "json" | "text" }): Promise<unknown>;
  put?: (
    key: string,
    value: string,
    opts?: { expirationTtl?: number },
  ) => Promise<void>;
}

async function readYelpMetadata(
  ctx: AdapterContext,
): Promise<YelpMetadata | null> {
  const env = ctx.env as unknown as { KV?: KvGetBinding };
  const kv = env.KV;
  if (!kv || typeof kv.get !== "function") return null;
  try {
    const raw = await kv.get(`yelp:meta:${ctx.integrationId}`, {
      type: "json",
    });
    if (!raw || typeof raw !== "object") return null;
    const obj = raw as Record<string, unknown>;
    if (typeof obj.businessId !== "string") return null;
    return {
      businessId: obj.businessId,
      name: typeof obj.name === "string" ? obj.name : "",
      alias: typeof obj.alias === "string" ? obj.alias : "",
      url: typeof obj.url === "string" ? obj.url : "",
      reviewsEndpointEntitled:
        typeof obj.reviewsEndpointEntitled === "boolean"
          ? obj.reviewsEndpointEntitled
          : false,
      last403At:
        typeof obj.last403At === "string" ? obj.last403At : undefined,
      address: obj.address as YelpMetadata["address"],
      categories: obj.categories as YelpMetadata["categories"],
      price: (obj.price as YelpMetadata["price"]) ?? null,
      isClaimed: (obj.isClaimed as YelpMetadata["isClaimed"]) ?? null,
      isClosed: (obj.isClosed as YelpMetadata["isClosed"]) ?? false,
      coordinates: obj.coordinates as YelpMetadata["coordinates"],
      tier: obj.tier as YelpMetadata["tier"],
      discoveredAt:
        typeof obj.discoveredAt === "string" ? obj.discoveredAt : undefined,
      businessInput:
        typeof obj.businessInput === "string" ? obj.businessInput : undefined,
    };
  } catch {
    return null;
  }
}

/**
 * Round 2 review fix (Edge Case Hunter Finding 11): patch the per-integration
 * metadata KV side-channel with concurrency-aware semantics.
 *
 * The previous unguarded read-modify-write was vulnerable to a connect/fetch
 * race: a stale-baseline fetchMetrics that saw `reviewsEndpointEntitled:
 * false` could overwrite a fresh connect's Plus-tier discovery.
 *
 * Mitigation: callers MAY pass `observedDiscoveredAt` (the `discoveredAt`
 * timestamp they read at the start of the operation). The patch is skipped
 * when the CURRENT KV `discoveredAt` is strictly newer than the observed one
 * — meaning someone else (a fresh connect) wrote in between. The patch
 * still applies when `observedDiscoveredAt` is omitted (legacy callers).
 */
async function writeYelpMetadataPatch(
  ctx: AdapterContext,
  patch: Partial<YelpMetadata>,
  options: { observedDiscoveredAt?: string } = {},
): Promise<void> {
  const env = ctx.env as unknown as { KV?: KvGetBinding };
  const kv = env.KV;
  if (!kv || typeof kv.get !== "function" || typeof kv.put !== "function") return;
  try {
    const current = (await readYelpMetadata(ctx)) ?? null;
    if (!current) return; // No baseline — nothing to patch.
    // Round 3 review fix (Edge Case Hunter Medium Finding 2): if either
    // timestamp is unparseable (KV corruption, malformed write), skip the
    // staleness guard entirely and let the patch apply — better than
    // silently degrading to "no protection" via NaN compare.
    const currentParsed = current.discoveredAt
      ? Date.parse(current.discoveredAt)
      : Number.NaN;
    const observedParsed = options.observedDiscoveredAt
      ? Date.parse(options.observedDiscoveredAt)
      : Number.NaN;
    if (
      options.observedDiscoveredAt &&
      current.discoveredAt &&
      Number.isFinite(currentParsed) &&
      Number.isFinite(observedParsed) &&
      currentParsed > observedParsed
    ) {
      // A newer write landed between our read and this patch (e.g. a fresh
      // connect re-discovered tier entitlement). Abort the patch so we
      // don't clobber the newer state.
      console.log(
        JSON.stringify({
          event: "yelp_metadata_patch_skipped_stale_baseline",
          integrationId: ctx.integrationId,
          tenantId: ctx.tenantId,
          currentDiscoveredAt: current.discoveredAt,
          observedDiscoveredAt: options.observedDiscoveredAt,
        }),
      );
      return;
    }
    const merged: YelpMetadata = { ...current, ...patch };
    await kv.put(`yelp:meta:${ctx.integrationId}`, JSON.stringify(merged));
  } catch {
    // Best-effort patch — never abort fetchMetrics on KV write failure.
  }
}

// ============================================================================
// Story 23.6 — Fixture-mode helper (loadYelpFixture)
// ============================================================================

/**
 * Story 23.6 Task 5 — fixture-mode loader. Invoked from the very first
 * statement of `fetchMetrics` when `ctx.fixtures === true`. Mirrors the
 * Trustpilot 23.4 + GR 23.2 + CTM 22.4 + CallRail 22.2 patterns with FOUR
 * divergences from Trustpilot 23.4:
 *
 *   1. GreenLife `platforms[]` pre-wired by Story 23.5 — SAME as Trustpilot
 *      23.4's pre-wired posture.
 *   2. NO 5th `_reportType` discriminator — DIVERGENT from Trustpilot 23.4's
 *      `contentIntegrityFlag` 5th discriminator. The build-to-sell wedge
 *      exploits the EXISTING `ratingSnapshot` discriminator that Yelp
 *      Fusion's API CANNOT match because there's no historical endpoint.
 *   3. `ratingSnapshot` rows ARE date-filtered, aggregate + reviewExcerpt
 *      rows SKIP date-filter — DIVERGENT from Trustpilot 23.4 which
 *      date-filters per-review rows. Yelp Fusion's `/v3/businesses/{id}`
 *      and `/v3/businesses/{id}/reviews` endpoints have NO server-side
 *      date filter, and the production adapter does NOT apply a client-
 *      side date filter to per-excerpt rows. The `ratingSnapshot` rows
 *      ARE date-filtered because the production adapter passes
 *      `startDate/endDate` to `readYelpRatingSnapshots` at yelp.ts:637-643.
 *      Round 2 Pre-mortem D invariant.
 *   4. Force-stamps `metadata.reviewsEndpointEntitled: true` + `tier:
 *      "plus"` — Story 23.6 NEW invariant; parallel to Trustpilot 23.4's
 *      `privateApiEntitled: true` force-stamp. Round 4 ADR-D.
 *
 * Structured-log telemetry — exactly ONE TERMINAL event fires:
 *   - `yelp_demo_fixture_loaded` (success — terminal)
 *   - `yelp_demo_fixture_missing` (KV miss — terminal)
 *   - `yelp_demo_fixture_binding_missing` (KV or DB missing — terminal)
 *   - `yelp_demo_fixture_resolver_failed` (getClientSlug throw — terminal)
 *   - `yelp_demo_fixture_invalid_context` (empty tenantId/clientId — terminal)
 *   - `yelp_demo_fixture_corrupt` (Round-1 review fix; shape-validation
 *     rejection of KV poisoning — terminal; returns `invalid_response`)
 *   - `yelp_demo_fixture_missing_business_id` (Round-2 review fix;
 *     cross-tenant leak defense when both metadata.businessId AND
 *     accountId are missing — terminal; returns `invalid_response`)
 *
 * Defensive/informational events that MAY co-fire alongside `_loaded`:
 *   - `yelp_demo_fixture_undated_rows_dropped` (Round-1 review fix;
 *     informational counter when ratingSnapshot rows have invalid
 *     `snapshotDate`; non-terminal; payload `{ droppedCount }`)
 *
 * Round 3 Red Team #1 invariant: fixture mode MUST NOT touch the
 * `yelp:meta:*` / `yelp:budget:*` / `yelp:budget:healthcheck:*` KV
 * namespaces — the helper only reads `fixture:yelp:{slug}` via the FDL
 * `getFixtureFromKV` helper (which scopes via the SQL-layer
 * `getClientSlug` tenant-scoped lookup). And the D1 `yelp_rating_snapshots`
 * table is NEVER touched in fixture mode (the 45 snapshot records come
 * EXCLUSIVELY from the fixture JSON).
 */
async function loadYelpFixture(
  ctx: AdapterContext,
  dateRange: { start: Date; end: Date },
): Promise<AdapterResult<AdapterMetrics>> {
  const env = ctx.env as unknown as Record<string, unknown>;
  const kv = env.KV as unknown;
  const db = env.DB as unknown;

  if (!kv || typeof (kv as { get?: unknown }).get !== "function") {
    console.log(
      JSON.stringify({
        event: "yelp_demo_fixture_binding_missing",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        missingBinding: "KV",
        timestamp: new Date().toISOString(),
      }),
    );
    return adapterErr({
      code: "permission_denied",
      message: "Missing KV binding — cannot load Yelp demo fixture",
      retryable: false,
    });
  }
  if (!db) {
    console.log(
      JSON.stringify({
        event: "yelp_demo_fixture_binding_missing",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        missingBinding: "DB",
        timestamp: new Date().toISOString(),
      }),
    );
    return adapterErr({
      code: "permission_denied",
      message: "Missing DB binding — cannot resolve Yelp demo client slug",
      retryable: false,
    });
  }

  if (
    typeof ctx.tenantId !== "string" ||
    ctx.tenantId.length === 0 ||
    typeof ctx.clientId !== "string" ||
    ctx.clientId.length === 0
  ) {
    console.log(
      JSON.stringify({
        event: "yelp_demo_fixture_invalid_context",
        integrationId: ctx.integrationId,
        missingTenantId:
          typeof ctx.tenantId !== "string" || ctx.tenantId.length === 0,
        missingClientId:
          typeof ctx.clientId !== "string" || ctx.clientId.length === 0,
        timestamp: new Date().toISOString(),
      }),
    );
    return adapterErr({
      code: "permission_denied",
      message:
        "Yelp demo fixture: ctx.tenantId and ctx.clientId must be non-empty strings",
      retryable: false,
    });
  }

  const { getFixtureFromKV } = await import("../fixtures/kv-loader");
  const { getClientSlug } = await import("../fixtures/demo-fetch");

  let clientSlug: string;
  try {
    clientSlug = await getClientSlug(
      db as Parameters<typeof getClientSlug>[0],
      ctx.tenantId,
      ctx.clientId,
    );
  } catch (err) {
    console.log(
      JSON.stringify({
        event: "yelp_demo_fixture_resolver_failed",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        clientId: ctx.clientId,
        errorClass: err instanceof Error ? err.constructor.name : typeof err,
        timestamp: new Date().toISOString(),
      }),
    );
    return adapterErr({
      code: "account_not_found",
      message: `Yelp demo client not found: ${err instanceof Error ? err.message : String(err)}`,
      retryable: false,
    });
  }

  const startedAt = Date.now();
  const metrics = await getFixtureFromKV(
    kv as Parameters<typeof getFixtureFromKV>[0],
    "yelp",
    clientSlug,
  );

  if (metrics === null) {
    const kvKey = `fixture:yelp:${clientSlug}`;
    console.log(
      JSON.stringify({
        event: "yelp_demo_fixture_missing",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        clientSlug,
        kvKey,
        timestamp: new Date().toISOString(),
      }),
    );
    return adapterErr({
      code: "account_not_found",
      message: `Yelp demo fixture not yet uploaded for client ${clientSlug}; run pnpm fixtures:upload`,
      retryable: false,
    });
  }

  // Shape validation — defense against KV poisoning / schema drift
  // (matches Trustpilot 23.4 Round 1+2 review fixes). Round-1 review
  // fix — read `platform` and `records` via safe accessors so the
  // corrupt-log path doesn't crash on non-object payloads. Round-2
  // review fix (Edge Case Hunter #8) — reject empty `records: []`
  // arrays as corrupt; a valid Yelp fixture has at minimum 5 aggregate
  // rows (1 businessDetails + 1 reviewAggregation + 3 reviewExcerpt
  // before any ratingSnapshot rows). Round-2 review fix (Edge Case
  // Hunter #9 + Blind Hunter #8) — reject array-as-object payloads
  // explicitly so the spread doesn't smear numeric indices.
  const metricsObj =
    typeof metrics === "object" &&
    metrics !== null &&
    !Array.isArray(metrics)
      ? (metrics as unknown as Record<string, unknown>)
      : null;
  const isValidShape =
    metricsObj !== null &&
    metricsObj.platform === "yelp" &&
    Array.isArray(metricsObj.records) &&
    (metricsObj.records as ReadonlyArray<unknown>).length > 0 &&
    (metricsObj.records as ReadonlyArray<unknown>).every(
      (r) => r !== null && typeof r === "object" && !Array.isArray(r),
    );
  if (!isValidShape) {
    const cachedPlatform = metricsObj ? metricsObj.platform : undefined;
    const recordsValue = metricsObj ? metricsObj.records : undefined;
    console.log(
      JSON.stringify({
        event: "yelp_demo_fixture_corrupt",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        clientSlug,
        kvKey: `fixture:yelp:${clientSlug}`,
        cachedPlatform,
        recordsType: Array.isArray(recordsValue)
          ? "array"
          : typeof recordsValue,
        timestamp: new Date().toISOString(),
      }),
    );
    return adapterErr({
      code: "invalid_response",
      message: `Yelp demo fixture for client ${clientSlug} is corrupt (wrong platform or non-array records); re-run pnpm fixtures:upload`,
      retryable: false,
    });
  }

  // Round 2 Pre-mortem D — apply client-side date filter to `ratingSnapshot`
  // rows ONLY (matches production parity at yelp.ts:637-643 where the
  // production adapter passes `startDate/endDate` to `readYelpRatingSnapshots`).
  // Aggregate rows (businessDetails / reviewAggregation) reflect point-in-
  // time values regardless of dateRange. ReviewExcerpt rows reflect the
  // 3 most recent excerpts regardless of dateRange (Yelp Fusion has NO
  // server-side date filter on /v3/businesses/{id}/reviews).
  //
  // Round-3 review fix (Blind Hunter #2) — compare on the YYYY-MM-DD
  // string axis (lexical UTC-date compare) exactly mirroring the
  // production SQL `WHERE snapshot_date >= ? AND snapshot_date <= ?`.
  // This guarantees demo-vs-prod parity even when the caller passes
  // sub-day-precision Date instances (e.g., a noon-UTC dateRange.start
  // would have previously caused fixture-mode to drop a same-day
  // snapshot that production keeps).
  //
  // Round-1 review fix — the date-filter bounds are INCLUSIVE on both
  // sides. The generator never emits a "today" snapshot (Round 3 Red
  // Team Attack #2 — today's snapshot is written by the production
  // adapter at fetchMetrics time, NEVER seeded in the fixture). The
  // generator-side guard at Story 23.6 AC #6 invariant tests that
  // today's snapshot is NOT in the fixture (test "snapshot stream date
  // boundary").
  const startDateStr = dateRange.start.toISOString().slice(0, 10);
  const endDateStr = dateRange.end.toISOString().slice(0, 10);
  let undatedDroppedCount = 0;
  const filteredRecords = (metricsObj.records as ReadonlyArray<unknown>).filter(
    (r) => {
      const rec = r as { _reportType?: string; snapshotDate?: unknown };
      if (rec._reportType !== "ratingSnapshot") return true; // aggregate / excerpt row — pass through
      if (typeof rec.snapshotDate !== "string") {
        undatedDroppedCount++;
        return false;
      }
      // snapshotDate is already YYYY-MM-DD UTC. Validate format via
      // Date.parse round-trip — defends against malformed strings like
      // "2026-99-99" or "yesterday".
      const t = Date.parse(`${rec.snapshotDate}T00:00:00.000Z`);
      if (!Number.isFinite(t)) {
        undatedDroppedCount++;
        return false;
      }
      // Lexical YYYY-MM-DD compare matches production SQL behavior
      return rec.snapshotDate >= startDateStr && rec.snapshotDate <= endDateStr;
    },
  );
  if (undatedDroppedCount > 0) {
    console.log(
      JSON.stringify({
        event: "yelp_demo_fixture_undated_rows_dropped",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        clientSlug,
        droppedCount: undatedDroppedCount,
        timestamp: new Date().toISOString(),
      }),
    );
  }

  // Round 2 review fix — multi-tenant safety. Source `businessId` and
  // `businessAlias` from the cached fixture metadata when present, falling
  // back to `metrics.accountId` only when metadata is absent.
  //
  // Round-2 review fix (Edge Case Hunter #9 + #13) — array-as-metadata
  // guard (an array passes `typeof === "object"` + `!== null` but should
  // not be spread as a metadata object). Also remove the GreenLife
  // hardcoded final fallback — if BOTH cached metadata and accountId
  // are missing, return adapter error rather than silently inherit
  // GreenLife's identifiers into a different tenant's demo (cross-
  // tenant leak prevention).
  const cachedMeta =
    typeof metricsObj.metadata === "object" &&
    metricsObj.metadata !== null &&
    !Array.isArray(metricsObj.metadata)
      ? (metricsObj.metadata as Record<string, unknown>)
      : {};
  const cachedAccountId =
    typeof metricsObj.accountId === "string" ? metricsObj.accountId : "";
  const cachedBusinessId =
    typeof cachedMeta.businessId === "string" &&
    cachedMeta.businessId.length > 0
      ? cachedMeta.businessId
      : cachedAccountId.length > 0
        ? cachedAccountId
        : null;
  if (cachedBusinessId === null) {
    console.log(
      JSON.stringify({
        event: "yelp_demo_fixture_missing_business_id",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        clientSlug,
        kvKey: `fixture:yelp:${clientSlug}`,
        timestamp: new Date().toISOString(),
      }),
    );
    return adapterErr({
      code: "invalid_response",
      message: `Yelp demo fixture for client ${clientSlug} is missing both metadata.businessId and accountId; re-run pnpm fixtures:generate + pnpm fixtures:upload`,
      retryable: false,
    });
  }
  const resolvedBusinessId = cachedBusinessId;
  const resolvedBusinessAlias =
    typeof cachedMeta.businessAlias === "string" &&
    cachedMeta.businessAlias.length > 0
      ? cachedMeta.businessAlias
      : "greenlife-wellness-portland";

  // Round-1 review fix — defensively parse `fetchedAt`. The kv-loader
  // rehydrates ISO strings to Date instances via `new Date(parsed.fetchedAt)`,
  // but a corrupt/missing value would yield Invalid Date. Accept both
  // the rehydrated Date instance and a raw ISO string for resilience;
  // fall through to the in-flight `completedAt` only when both fail.
  const completedAt = new Date();
  const rawFetchedAt = metricsObj.fetchedAt;
  let fetchedAt: Date = completedAt;
  if (rawFetchedAt instanceof Date && Number.isFinite(rawFetchedAt.getTime())) {
    fetchedAt = rawFetchedAt;
  } else if (typeof rawFetchedAt === "string") {
    const parsed = new Date(rawFetchedAt);
    if (Number.isFinite(parsed.getTime())) fetchedAt = parsed;
  }

  // Round-1 review fix — merge into `_yelp` rather than replace, so
  // future schema additions under `cachedMeta._yelp.*` are preserved.
  // Round-2 review fix (Edge Case Hunter #9) — Array.isArray guard.
  const cachedYelp =
    typeof cachedMeta._yelp === "object" &&
    cachedMeta._yelp !== null &&
    !Array.isArray(cachedMeta._yelp)
      ? (cachedMeta._yelp as Record<string, unknown>)
      : {};

  // Preserve all spread keys from the cached metrics envelope; the
  // resolver wrote them at FDL generate time and downstream consumers
  // may depend on warnings / dateRange / etc.
  //
  // Round-3 review fix (Edge Case Hunter #3) — `accountId` is sourced
  // from `ctx.accountId` (the per-request identifier) FIRST, then from
  // the resolved-business-id which itself was derived from cachedMeta +
  // cachedAccountId under multi-tenant safety guards above. We DO NOT
  // fall back to `cachedAccountId` independently of the businessId
  // resolution chain — that would re-open the cross-tenant identifier
  // leak that the businessId guard already closed. `resolvedBusinessId`
  // is the canonical, integrity-checked identifier.
  const ctxAccountId =
    typeof ctx.accountId === "string" && ctx.accountId.length > 0
      ? ctx.accountId
      : null;
  const result: AdapterMetrics = {
    ...(metricsObj as Partial<AdapterMetrics>),
    platform: "yelp",
    accountId: ctxAccountId ?? resolvedBusinessId,
    dateRange,
    records: filteredRecords as AdapterMetrics["records"],
    metadata: {
      ...cachedMeta,
      // Force-stamped invariants per AC #8 Step 10
      dataFreshnessLagHours: 0,
      // Round 4 ADR-D — fixture forces Plus-tier elevation
      reviewsEndpointEntitled: true,
      tier: "plus",
      businessId: resolvedBusinessId,
      businessAlias: resolvedBusinessAlias,
      _yelp: {
        ...cachedYelp,
        reviewsCallSucceeded: true,
        dailyBudgetUsed: 0,
      },
    },
    recordCount: filteredRecords.length,
    fetchedAt,
  } as AdapterMetrics;

  console.log(
    JSON.stringify({
      event: "yelp_demo_fixture_loaded",
      integrationId: ctx.integrationId,
      tenantId: ctx.tenantId,
      clientSlug,
      recordCount: result.recordCount,
      businessId: resolvedBusinessId,
      reviewsEndpointEntitled: true,
      tier: "plus",
      undatedDroppedCount,
      durationMs: completedAt.getTime() - startedAt,
      timestamp: completedAt.toISOString(),
    }),
  );

  return adapterOk(result);
}

// ============================================================================
// Yelp Adapter
// ============================================================================

export const yelpAdapter: IntegrationAdapter = {
  platform: PLATFORM,
  rateLimits: RATE_LIMITS,
  authType: "api-key",

  // ---- fetchMetrics ------------------------------------------------------
  async fetchMetrics(
    ctx,
    credentials,
    dateRange,
  ): Promise<AdapterResult<AdapterMetrics>> {
    // Story 23.6 Round-1 review fix — validate dateRange BEFORE both the
    // fixture short-circuit and the production path. Inverted ranges
    // would silently drop all snapshot results in the fixture path's
    // date-filter, and silently drop all historical-snapshot rows in
    // the production path (yelp.ts:637-643 readYelpRatingSnapshots
    // returns empty on inverted ranges). Guard at top — mirrors
    // Trustpilot 23.4's Round 2 review fix invariant.
    if (
      !(dateRange.start instanceof Date) ||
      Number.isNaN(dateRange.start.getTime()) ||
      !(dateRange.end instanceof Date) ||
      Number.isNaN(dateRange.end.getTime())
    ) {
      return adapterErr({
        code: "invalid_request",
        message: "Yelp fetchMetrics received an invalid Date in dateRange.",
        retryable: false,
      });
    }
    if (dateRange.start.getTime() > dateRange.end.getTime()) {
      return adapterErr({
        code: "invalid_request",
        message:
          "Yelp fetchMetrics received an inverted dateRange (start > end).",
        retryable: false,
      });
    }

    // Story 23.6 — fixture-mode short-circuit. MUST run BEFORE the
    // `credentials.accessToken` read, BEFORE the budget pre-flight,
    // BEFORE the businessId resolution, BEFORE any KV reads against
    // `yelp:meta:*` or `yelp:budget:*` namespaces. Mirrors Trustpilot
    // 23.4 + GR 23.2 + CTM 22.4 + CallRail 22.2 short-circuit patterns.
    // Drift-log fires when `ctx.fixtures` is truthy-but-not-strictly-`true`
    // (defensive — operator-facing).
    const rawFixtures = (ctx as unknown as { fixtures?: unknown }).fixtures;
    if (
      rawFixtures !== undefined &&
      rawFixtures !== true &&
      rawFixtures !== false
    ) {
      console.log(
        JSON.stringify({
          event: "yelp_fixtures_flag_unexpected_type",
          integrationId: ctx.integrationId,
          tenantId: ctx.tenantId,
          actualType: typeof rawFixtures,
          timestamp: new Date().toISOString(),
        }),
      );
    }
    const fixturesEnabled = rawFixtures === true;
    if (fixturesEnabled) {
      return loadYelpFixture(ctx, dateRange);
    }

    const apiKey = credentials.accessToken;
    const env = ctx.env as unknown as { KV?: YelpBudgetKv & KvGetBinding; DB?: YelpD1Binding };
    const kv = env.KV;
    const db = env.DB;

    // Round 1 review fix (Blind Hunter + Edge Case Hunter):
    // empty/non-string apiKey must short-circuit BEFORE building any
    // Authorization header — an empty Bearer value sails through the
    // redactor's length-0 short-circuit and produces an unredacted log path.
    if (typeof apiKey !== "string" || apiKey.length === 0) {
      return adapterErr({
        code: "token_invalid",
        message: "Yelp API Key is missing or empty.",
        retryable: false,
      });
    }

    // Note: dateRange validation lifted to the top of fetchMetrics (Story
    // 23.6 Round-1 review fix) — the inverted-range and NaN-Date guards
    // now run BEFORE the fixture short-circuit so both production and
    // fixture paths reject malformed ranges identically.

    // ---- Pre-flight: read metadata + validate businessId -----------------
    const metadata = await readYelpMetadata(ctx);
    const businessId = metadata?.businessId ?? ctx.accountId;
    if (!businessId) {
      return adapterErr({
        code: "invalid_request",
        message: "Yelp businessId missing from KV metadata AND ctx.accountId.",
        retryable: false,
      });
    }
    // Cross-check (Round 3 Red Team Attack #5 — business-ID enumeration defense)
    if (
      ctx.accountId &&
      metadata &&
      ctx.accountId !== metadata.businessId
    ) {
      return adapterErr({
        code: "invalid_request",
        message:
          "Yelp ctx.accountId does not match metadata.businessId — refusing to fetch.",
        retryable: false,
      });
    }

    // Round 1 review fix (Edge Case Hunter Finding 10):
    // pin a single `now` Date through fetchMetrics so the budget-key date AND
    // snapshot-date AND prior-snapshot query all see the same UTC day even
    // when the function spans a midnight rollover. Without this pin the
    // pre-flight check at 23:59:58 reads today's key but the increment at
    // 00:00:01 writes tomorrow's key — granting a free budget across the
    // rollover.
    const nowAtFetchStart = new Date();

    // ---- Pre-flight: check daily budget ---------------------------------
    if (kv) {
      const budget = await checkYelpDailyBudget(kv, ctx.tenantId, nowAtFetchStart);
      if (!budget.available) {
        return adapterErr({
          code: "rate_limited",
          message:
            `Yelp daily call budget exhausted (${budget.used}/${budget.ceiling}) — resets at midnight UTC.`,
          retryable: false,
        });
      }
    }

    // ---- Endpoint Group A — Business Details (always-available) ---------
    // Round 2 review fix (Blind Hunter): convert any synchronous TypeError
    // from `assertBusinessId` into an AdapterErrorResult so a malformed
    // businessId never escapes the AdapterResult contract.
    let detailsResult: Awaited<ReturnType<typeof fetchYelpBusinessDetails>>;
    try {
      detailsResult = await fetchYelpBusinessDetails(businessId, apiKey);
    } catch (err) {
      if (err instanceof TypeError) {
        return adapterErr({
          code: "invalid_request",
          message: sanitizeYelpErrorMessage(err.message, apiKey),
          retryable: false,
        });
      }
      throw err;
    }
    if (!detailsResult.success) {
      return detailsResult;
    }
    if (kv) {
      await incrementYelpDailyBudget(kv, ctx.tenantId, nowAtFetchStart);
    }
    const details = detailsResult.data;

    // ---- Endpoint Group B — Reviews (paid-tier gated) -------------------
    let reviewsResponse: YelpReviewsResponse | null = null;
    // Round 2 review fix (Acceptance Auditor): preserve the persistent
    // tier-entitlement state separately from the per-call control-flow flag
    // so the final structured log reports the integration's true tier and
    // doesn't conflate "budget-skipped" with "tier-downgraded" in
    // observability dashboards.
    const tierEntitled = metadata?.reviewsEndpointEntitled ?? false;
    let attemptReviewsCall = tierEntitled;
    if (attemptReviewsCall) {
      // Round 1 review fix (Blind Hunter + Edge Case Hunter Finding 2):
      // re-check the budget BEFORE the second API call so a fetchMetrics that
      // passed pre-flight at used=249 cannot push usage past the 250 ceiling
      // by issuing both Group A AND Group B calls without re-gating.
      if (kv) {
        const budget = await checkYelpDailyBudget(kv, ctx.tenantId, nowAtFetchStart);
        if (!budget.available) {
          // Don't fail the whole fetch — we already got the business-details
          // payload. Skip Group B and continue with business-details-only mode.
          console.log(
            JSON.stringify({
              event: "yelp_reviews_endpoint_skipped_budget_ceiling",
              integrationId: ctx.integrationId,
              tenantId: ctx.tenantId,
              budgetUsed: budget.used,
              budgetCeiling: budget.ceiling,
              timestamp: nowAtFetchStart.toISOString(),
            }),
          );
          attemptReviewsCall = false;
        }
      }
    }
    if (attemptReviewsCall) {
      const reviewsResult = await fetchYelpReviews(businessId, apiKey, 3);
      if (reviewsResult.success) {
        reviewsResponse = reviewsResult.data;
        if (kv) {
          await incrementYelpDailyBudget(kv, ctx.tenantId, nowAtFetchStart);
        }
        // Round 3 review fix (Edge Case Hunter + Blind Hunter Medium):
        // a successful reviews fetch clears any prior `last403At` strike so
        // a 23h-old transient 403 followed by 200 OK doesn't later trip
        // a "second_within_24h" false-positive 25h after the original 403.
        if (metadata?.last403At) {
          await writeYelpMetadataPatch(
            ctx,
            { last403At: undefined },
            { observedDiscoveredAt: metadata?.discoveredAt },
          );
        }
      } else if (
        reviewsResult.error.providerStatus === 403 ||
        reviewsResult.error.providerStatus === 401
      ) {
        // Round 3 review fix (Edge Case Hunter Finding 9): 401 mid-fetch
        // AFTER a successful business-details 200 means the customer's
        // tier-grace expired or the reviews-endpoint scope was revoked
        // post-issue. Treat the same as 403 — soft-skip Group B and keep
        // serving business-details data instead of failing the whole
        // fetchMetrics call. (Connect-time 401 on probe still hard-fails
        // because the customer hasn't yet committed to a Yelp connection
        // and we can't validate any tier signal.)
        // Round 2 review fix (Edge Case Hunter): 2-strike threshold —
        // require a SECOND 403 within RECENT_403_WINDOW_MS before permanently
        // flipping `reviewsEndpointEntitled` to false. Defends against
        // transient 403s (CDN edge mis-routing, brief tier-system glitch)
        // that would otherwise lock a paid-tier customer into Starter mode
        // until they hit the connect wizard again.
        const nowMs = nowAtFetchStart.getTime();
        const priorMs = metadata?.last403At
          ? Date.parse(metadata.last403At)
          : Number.NaN;
        const isRecentRepeat =
          Number.isFinite(priorMs) && nowMs - priorMs < RECENT_403_WINDOW_MS;
        // Round 2 review fix (Edge Case Hunter Finding 11): pass the
        // observed discoveredAt so a concurrent connect re-discovery (which
        // would have written a NEWER metadata blob) can't be clobbered by
        // our stale-baseline patch.
        const providerStatus = reviewsResult.error.providerStatus;
        if (isRecentRepeat) {
          console.log(
            JSON.stringify({
              event: "yelp_reviews_endpoint_downgrade",
              integrationId: ctx.integrationId,
              tenantId: ctx.tenantId,
              businessId,
              providerStatus,
              strike: "second_within_24h",
              timestamp: nowAtFetchStart.toISOString(),
            }),
          );
          await writeYelpMetadataPatch(
            ctx,
            {
              reviewsEndpointEntitled: false,
              last403At: nowAtFetchStart.toISOString(),
            },
            { observedDiscoveredAt: metadata?.discoveredAt },
          );
        } else {
          console.log(
            JSON.stringify({
              event: "yelp_reviews_endpoint_403_first_strike",
              integrationId: ctx.integrationId,
              tenantId: ctx.tenantId,
              businessId,
              providerStatus,
              priorLast403At: metadata?.last403At ?? null,
              timestamp: nowAtFetchStart.toISOString(),
            }),
          );
          // Record the strike but do NOT flip the entitlement flag yet.
          await writeYelpMetadataPatch(
            ctx,
            { last403At: nowAtFetchStart.toISOString() },
            { observedDiscoveredAt: metadata?.discoveredAt },
          );
        }
        attemptReviewsCall = false;
      } else {
        // Other failures propagate (429/5xx) — the adapter cannot proceed
        // partially without compromising the data contract. Before
        // propagating, scrub any PII the upstream redactor may have missed
        // (defense-in-depth — sanitizeYelpPii is the documented contract
        // for any record-shaped payload leaving the fetchMetrics path).
        // The error itself contains no records, but a hostile provider body
        // could in theory be echoed; this future-proofs against record-leak
        // when the error payload starts including record shapes.
        const scrubbed = sanitizeYelpPii({
          providerStatus: reviewsResult.error.providerStatus,
          message: reviewsResult.error.message,
        });
        console.warn(
          "[yelp_reviews_unexpected_failure]",
          JSON.stringify(scrubbed),
        );
        return reviewsResult;
      }
    }

    // ---- Endpoint Group C — Snapshot persistence ------------------------
    // Round 1 review fix (Edge Case Hunter Finding 10): use the pinned
    // nowAtFetchStart so the snapshot-day, the budget-day, and the prior-day
    // query all read from the same UTC date — even when the function spans
    // the midnight rollover.
    const todayUtc = utcYyyyMmDd(nowAtFetchStart);
    let priorSnapshot: Awaited<
      ReturnType<typeof readYelpRatingSnapshotPriorDay>
    > = null;
    let historicalSnapshots: Awaited<
      ReturnType<typeof readYelpRatingSnapshots>
    > = [];
    // Round 1 review fix (Blind Hunter Finding 2 / Edge Case Hunter Finding 12):
    // refuse to persist a snapshot row when Yelp returned a non-finite
    // rating/review_count (provider-shape drift would otherwise pollute the
    // trend chart with permanent rating=0 rows).
    const ratingIsFinite =
      typeof details.rating === "number" && Number.isFinite(details.rating);
    const reviewCountIsFinite =
      typeof details.review_count === "number" &&
      Number.isFinite(details.review_count);
    if (db) {
      // Snapshot WRITE only when the provider returned finite numerics —
      // avoids polluting the trend chart with rating=0 rows on shape drift.
      if (ratingIsFinite && reviewCountIsFinite) {
        try {
          await writeYelpRatingSnapshot(db, {
            tenantId: ctx.tenantId,
            integrationId: ctx.integrationId,
            businessId,
            rating: details.rating,
            reviewCount: details.review_count,
            snapshotDate: todayUtc,
            // Round 3 review fix (Edge Case Hunter HIGH): pin capturedAt to
            // nowAtFetchStart so a fetch that spans midnight UTC doesn't
            // emit a snapshot row whose capturedAt is in a different
            // calendar day than its snapshotDate.
            capturedAt: nowAtFetchStart.toISOString(),
          });
        } catch (err) {
          // Snapshot-write failure is non-fatal — log and continue.
          console.warn(
            "[yelp_snapshot_write_failed]",
            sanitizeYelpErrorMessage(
              err instanceof Error ? err.message : String(err),
              apiKey,
            ),
          );
        }
      } else {
        console.warn(
          "[yelp_snapshot_write_skipped_shape_drift]",
          JSON.stringify({
            integrationId: ctx.integrationId,
            tenantId: ctx.tenantId,
            businessId,
            ratingIsFinite,
            reviewCountIsFinite,
          }),
        );
      }
      // Reads always run (no risk of polluting data).
      try {
        priorSnapshot = await readYelpRatingSnapshotPriorDay(db, {
          tenantId: ctx.tenantId,
          integrationId: ctx.integrationId,
          businessId,
          today: todayUtc,
        });
      } catch {
        priorSnapshot = null;
      }
      try {
        historicalSnapshots = await readYelpRatingSnapshots(db, {
          tenantId: ctx.tenantId,
          integrationId: ctx.integrationId,
          businessId,
          startDate: utcYyyyMmDd(dateRange.start),
          endDate: utcYyyyMmDd(dateRange.end),
        });
      } catch {
        historicalSnapshots = [];
      }
    }

    // Story 23.6 Round-3 review fix — synthetic-aggregate metrics
    // (`ratingTrendDirection` + `reviewVelocityPerMonth`) need ≥ 31 days
    // of snapshot history to compute. The user-selected `dateRange` may
    // be narrower (e.g., last 7 days for a weekly report). Fetch a
    // SEPARATE 45-day window for synthetic-aggregate computation so the
    // preset #8 4th-slot KPI renders consistently regardless of user's
    // date selection. Falls back to the user-window snapshots on read
    // failure (best-effort observability).
    let syntheticHistoricalSnapshots: typeof historicalSnapshots = [];
    if (db) {
      try {
        const oneDayMs = 24 * 60 * 60 * 1000;
        const SYNTHETIC_WINDOW_DAYS = 45;
        const syntheticStartDate = utcYyyyMmDd(
          new Date(dateRange.end.getTime() - SYNTHETIC_WINDOW_DAYS * oneDayMs),
        );
        syntheticHistoricalSnapshots = await readYelpRatingSnapshots(db, {
          tenantId: ctx.tenantId,
          integrationId: ctx.integrationId,
          businessId,
          startDate: syntheticStartDate,
          endDate: utcYyyyMmDd(dateRange.end),
        });
      } catch {
        syntheticHistoricalSnapshots = historicalSnapshots;
      }
    }

    // ---- Build records ---------------------------------------------------
    const records: Record<string, unknown>[] = [];

    // 1 businessDetails record
    records.push(transformYelpBusinessDetails(details));

    // 1 reviewAggregation record (emitted EVEN on free tier per AC #3).
    // Story 23.6 — pass `syntheticHistoricalSnapshots` (≥45 days) for
    // the synthetic-aggregate metrics so preset #8 4th-slot KPI renders
    // regardless of the user's selected dateRange. The per-record
    // `historicalSnapshots` array (user-window-scoped) still flows
    // through the records pipeline below — that's the data source for
    // the line-chart presets (#4 + #5).
    records.push(
      buildYelpReviewAggregation({
        businessId,
        currentRating: details.rating,
        currentReviewCount: details.review_count,
        priorSnapshot,
        reviewsResponse,
        historicalSnapshots: syntheticHistoricalSnapshots,
      }),
    );

    // Story 23.6 Round-3 review fix — emit decrement event at adapter
    // boundary (with ctx in scope) when computeYelpReviewVelocityPerMonth
    // would have clamped to 0. The pure helper no longer logs; the
    // adapter does, with full tenant/integration/business correlation.
    if (
      typeof details.review_count === "number" &&
      Number.isFinite(details.review_count) &&
      syntheticHistoricalSnapshots.length > 0
    ) {
      const sortedSnaps = [...syntheticHistoricalSnapshots].sort((a, b) =>
        a.snapshotDate.localeCompare(b.snapshotDate),
      );
      const newest = sortedSnaps[sortedSnaps.length - 1];
      if (newest && details.review_count < newest.reviewCount) {
        console.log(
          JSON.stringify({
            event: "yelp_review_count_decrement_detected",
            integrationId: ctx.integrationId,
            tenantId: ctx.tenantId,
            businessId,
            currentReviewCount: details.review_count,
            priorReviewCount: newest.reviewCount,
            priorSnapshotDate: newest.snapshotDate,
            timestamp: nowAtFetchStart.toISOString(),
          }),
        );
      }
    }

    // N reviewExcerpt records (only when paid tier returns data)
    if (reviewsResponse && reviewsResponse.reviews.length > 0) {
      for (const review of reviewsResponse.reviews) {
        records.push(transformYelpReview(review, businessId));
      }
    }

    // M ratingSnapshot records (historical trend)
    for (const row of historicalSnapshots) {
      records.push(transformYelpRatingSnapshot(row));
    }

    // ---- Structured log -------------------------------------------------
    const dailyBudgetUsed = kv
      ? (await checkYelpDailyBudget(kv, ctx.tenantId, nowAtFetchStart)).used
      : 0;
    const ratingDelta =
      priorSnapshot !== null
        ? Math.round((details.rating - priorSnapshot.rating) * 100) / 100
        : null;
    const reviewCountDelta =
      priorSnapshot !== null
        ? details.review_count - priorSnapshot.reviewCount
        : null;
    // Round 2 review fix (Acceptance Auditor + Edge Case Hunter): emit the
    // PERSISTENT `reviewsEndpointEntitled` (from KV metadata) so operators
    // can distinguish "tier downgrade" from "budget-skipped this call".
    // `reviewsCallSucceeded` reports whether the actual call ran AND
    // returned data. `timestamp` and `startedAt` are pinned to
    // nowAtFetchStart so the forensic clock matches the budget/snapshot
    // keys even when the function spans a midnight rollover.
    console.log(
      JSON.stringify({
        event: "yelp_fetch_complete",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        businessId,
        alias: details.alias,
        reviewsEndpointEntitled: tierEntitled,
        reviewsCallSucceeded: reviewsResponse !== null,
        rating: details.rating,
        reviewCount: details.review_count,
        excerptsFetched: reviewsResponse?.reviews.length ?? 0,
        ratingDelta,
        reviewCountDelta,
        snapshotsRead: historicalSnapshots.length,
        dailyBudgetUsed,
        dailyBudgetCeiling: YELP_DAILY_BUDGET_CEILING,
        recordCount: records.length,
        startedAt: nowAtFetchStart.toISOString(),
        timestamp: nowAtFetchStart.toISOString(),
      }),
    );

    // ---- Return ----------------------------------------------------------
    return adapterOk({
      platform: PLATFORM,
      accountId: businessId,
      dateRange,
      records,
      // Round 3 review fix (Edge Case Hunter HIGH): pin fetchedAt to the
      // start of the fetch so downstream consumers see a consistent
      // forensic timestamp matching the snapshot + budget + log times.
      fetchedAt: nowAtFetchStart,
      recordCount: records.length,
      // Round 3 review fix (Edge Case Hunter HIGH): namespace the
      // yelp-specific observability fields under a `_yelp` side-channel so
      // generic widget-config consumers reading `metadata.reviewsCallSucceeded`
      // don't misclassify a budget-skipped fetch as a "broken" tile. The
      // canonical AdapterMetrics keys (`dataFreshnessLagHours`,
      // `reviewsEndpointEntitled`) stay at the top level for the widget
      // tier-gating UI; yelp-private fields live under `_yelp`.
      metadata: {
        dataFreshnessLagHours: 0,
        reviewsEndpointEntitled: tierEntitled,
        _yelp: {
          reviewsCallSucceeded: reviewsResponse !== null,
          dailyBudgetUsed,
        },
      },
    });
  },

  // ---- validateConnection -----------------------------------------------
  async validateConnection(
    ctx,
    credentials,
  ): Promise<AdapterResult<boolean>> {
    const apiKey = credentials.accessToken;
    // Round 1 review fix (Blind Hunter): empty apiKey short-circuit mirroring
    // fetchMetrics — never hand an empty Bearer to Yelp or the redactor.
    if (typeof apiKey !== "string" || apiKey.length === 0) {
      return adapterErr({
        code: "token_invalid",
        message: "Yelp API Key is missing or empty.",
        retryable: false,
      });
    }
    const metadata = await readYelpMetadata(ctx);
    const businessId = metadata?.businessId ?? ctx.accountId;
    if (!businessId) {
      return adapterErr({
        code: "invalid_request",
        message: "Yelp businessId missing — cannot validate connection.",
        retryable: false,
      });
    }

    const env = ctx.env as unknown as { KV?: YelpBudgetKv };
    // Round 2 review fix (Blind Hunter): pin `now` through the validateConn
    // call so the healthcheck-budget key is computed against the same UTC
    // date a single tick refers to — preventing midnight-rollover skew.
    const nowAtValidateStart = new Date();
    if (env.KV) {
      // AC #22: healthcheck increments a SEPARATE keyspace for
      // cost-allocation observability — NOT the main daily budget.
      try {
        await incrementYelpHealthcheckBudget(
          env.KV,
          ctx.tenantId,
          nowAtValidateStart,
        );
      } catch (err) {
        // Round 2 review fix (Blind Hunter): KV failures used to be
        // silently swallowed; log so ops can see KV degradation in the
        // healthcheck path.
        console.warn(
          "[yelp_healthcheck_budget_increment_failed]",
          err instanceof Error ? err.message : String(err),
        );
      }
    }

    const result = await fetchYelpBusinessDetails(businessId, apiKey);
    if (!result.success) {
      return adapterErr(result.error);
    }
    return adapterOk(true);
  },

  // ---- discoverAccounts -------------------------------------------------
  async discoverAccounts(
    ctx,
    credentials,
  ): Promise<AdapterResult<AccountInfo[]>> {
    const apiKey = credentials.accessToken;
    if (typeof apiKey !== "string" || apiKey.length === 0) {
      return adapterErr({
        code: "token_invalid",
        message: "Yelp API Key is missing or empty.",
        retryable: false,
      });
    }
    const metadata = await readYelpMetadata(ctx);
    const businessId = metadata?.businessId ?? ctx.accountId;
    if (!businessId) {
      return adapterErr({
        code: "invalid_request",
        message:
          "Yelp businessId missing — cannot discover accounts without a stored businessId.",
        retryable: false,
      });
    }
    const result = await fetchYelpBusinessDetails(businessId, apiKey);
    if (!result.success) {
      return adapterErr(result.error);
    }
    const details = result.data;
    const account: AccountInfo = {
      id: details.id,
      name: details.name,
      platform: PLATFORM,
      metadata: {
        alias: details.alias,
        url: details.url,
        isClaimed: details.is_claimed ?? null,
        isClosed: details.is_closed,
        rating: details.rating,
        reviewCount: details.review_count,
        country: details.location?.country ?? null,
      },
    };
    return adapterOk([account]);
  },

  // ---- getAvailableMetrics ----------------------------------------------
  getAvailableMetrics(): MetricDefinition[] {
    return [...YELP_METRICS];
  },

  // ---- getAvailableDimensions -------------------------------------------
  getAvailableDimensions(): DimensionDefinition[] {
    return [...YELP_DIMENSIONS];
  },

  // ---- discoverEndpoints (AC #13) ---------------------------------------
  async discoverEndpoints(
    ctx,
    credentials,
  ): Promise<AdapterResult<EndpointDiscoveryInfo>> {
    // Round 2 review fix (Blind Hunter): validate that a credential blob
    // was passed even though discoverEndpoints reads from KV-cached state.
    // Belt-and-braces — IDOR guard at the route is the primary defense, but
    // an empty apiKey here is always a programming error worth surfacing.
    if (
      typeof credentials.accessToken !== "string" ||
      credentials.accessToken.length === 0
    ) {
      return adapterErr({
        code: "token_invalid",
        message: "Yelp API Key is missing or empty.",
        retryable: false,
      });
    }
    const metadata = await readYelpMetadata(ctx);
    const reviewsEntitled = metadata?.reviewsEndpointEntitled === true;
    return adapterOk({
      endpoints: [
        { key: "businessDetails", status: "available" },
        reviewsEntitled
          ? { key: "reviews", status: "available" }
          : {
              key: "reviews",
              status: "subscription_required",
              subscriptionRequired:
                "Yelp Fusion Plus tier or higher required for review excerpts",
            },
        {
          key: "reviewHighlights",
          status: "not_configured",
          setupHint:
            "Reserved for future story (Plus+ tier curated snippets endpoint)",
        },
        {
          key: "transactions",
          status: "not_configured",
          setupHint:
            "Reserved for future story (transaction-enabled-businesses search)",
        },
        {
          key: "phoneSearch",
          status: "not_configured",
          setupHint:
            "Reserved for future story (phone-number-to-business lookup)",
        },
        {
          key: "events",
          status: "not_configured",
          setupHint: "Deprecated Yelp Events API — not implemented",
        },
      ],
    });
  },
};

/** Re-export the validateConnection method for the cron healthCheck handler. */
export const validateYelpConnection =
  yelpAdapter.validateConnection.bind(yelpAdapter);

